import { getValueFromLocalstore } from './localStorage.service';
import loggerService from './logger.service';

import analytics from '@/analytics';
import { LOCAL_STORAGE_KEYS } from '@/constants';
import { getStore } from '@/utils/storeAccess';

/***************************************/
// TYPES
/***************************************/
type Service =
  | 'mattermost'
  | 'apigateway'
  | 'agora'
  | 'serverSentEvent'
  | 'inHouseSimulive'
  | 'onDemandVideo'
  | 'liveHybridVideo';
type Events =
  | 'error'
  | 'retry'
  | 'connectionInitialized'
  | 'connectionEstablished'
  | 'disconnections'
  | 'averageConnectionTime'
  | 'terminated'
  | 'shouldBeUsed'
  | 'bufferStart'
  | 'bufferEnd'
  | 'bufferCount'
  | 'totalBufferDuration'
  | 'fpsDrop'
  | 'fpsDropCount'
  | 'droppedFrames'
  | 'liveLatency'
  | 'avgLiveLatency'
  | 'latencySampleCount'
  | 'firstConnectionTime'
  | 'seekCount'
  | 'nonFatalError'
  | 'nonFatalErrorCount'
  | 'fragLoaded'
  | 'fragLoadTimeTotal'
  | 'fragLoadTimeSamples'
  | 'avgFragLoadTime'
  | 'levelSwitchCount'
  | 'levelSwitchDownCount'
  | 'playbackRestart'
  | 'playbackRestartCount'
  | 'forwardSkipCount'
  | 'rewindCount'
  | 'pauseCount'
  | 'speedChangeCount'
  | 'syncCorrectionCount';

type EventRecord = number | boolean;
type TrackerServiceRecord = Record<Service, Record<Events, EventRecord>>;

/***************************************/
// Raw Tracker Service
/***************************************/
interface RawTrackerService {
  init: () => void;
  track: (
    service: Service,
    event: Events,
    options?: { timestamp?: number; isEnabled?: boolean; droppedFrames?: number; latency?: number }
  ) => void;
  addService: (service: Service) => void;
  pushData: (services: Service[]) => void;
}

/***************************************/
// Tracker Service Implementation
/***************************************/
class TrackerService implements RawTrackerService {
  private initializationTime: number;
  private eventServicesMap: TrackerServiceRecord;

  private initEventsData(): Record<Events, EventRecord> {
    return {
      retry: 0,
      error: 0,
      connectionInitialized: 0,
      connectionEstablished: 0,
      disconnections: 0,
      averageConnectionTime: 0,
      shouldBeUsed: false,
      terminated: false,
      bufferStart: 0,
      bufferEnd: 0,
      bufferCount: 0,
      totalBufferDuration: 0,
      fpsDrop: 0,
      fpsDropCount: 0,
      droppedFrames: 0,
      liveLatency: 0,
      avgLiveLatency: 0,
      latencySampleCount: 0,
      firstConnectionTime: 0,
      seekCount: 0,
      nonFatalError: 0,
      nonFatalErrorCount: 0,
      fragLoaded: 0,
      fragLoadTimeTotal: 0,
      fragLoadTimeSamples: 0,
      avgFragLoadTime: 0,
      levelSwitchCount: 0,
      levelSwitchDownCount: 0,
      playbackRestart: 0,
      playbackRestartCount: 0,
      forwardSkipCount: 0,
      rewindCount: 0,
      pauseCount: 0,
      speedChangeCount: 0,
      syncCorrectionCount: 0
    };
  }

  public init() {
    const services: Service[] = [
      'apigateway',
      'mattermost',
      'agora',
      'serverSentEvent',
      'inHouseSimulive',
      'onDemandVideo',
      'liveHybridVideo'
    ];
    this.eventServicesMap = {} as TrackerServiceRecord;
    services.forEach((service: Service) => {
      this.eventServicesMap[service] = this.initEventsData();
    });
    this.initializationTime = Date.now();
    window.addEventListener('beforeunload', this.pushAllData.bind(this));
    loggerService.log('[Tracker Service] initialized');
  }

  public track(
    service: Service,
    event: Events,
    options?: { timestamp?: number; isEnabled?: boolean; droppedFrames?: number; latency?: number; loadTime?: number }
  ) {
    try {
      if (!this.eventServicesMap[service]) {
        loggerService.warn('[Tracker Service] Service does not exist', service);
        return;
      }

      switch (event) {
        case 'retry':
        case 'error':
        case 'disconnections':
          this.eventServicesMap[service][event] = Number(this.eventServicesMap[service][event]) + 1;
          break;
        case 'connectionInitialized':
          if (options?.timestamp) {
            this.eventServicesMap[service][event] = options.timestamp;
          }
          break;
        case 'connectionEstablished': {
          const connectionInitialized = Number(this.eventServicesMap[service]['connectionInitialized']);
          const noOfConnections = Number(this.eventServicesMap[service][event]) + 1;
          const averageConnectionTime = Number(this.eventServicesMap[service]['averageConnectionTime']);

          if (options?.timestamp && connectionInitialized > 0) {
            const timestampInSeconds = options.timestamp;
            const timeDiff = timestampInSeconds - connectionInitialized;
            const averageTime: number = (timeDiff + averageConnectionTime * (noOfConnections - 1)) / noOfConnections;

            if (noOfConnections === 1) {
              this.eventServicesMap[service]['firstConnectionTime'] = timeDiff;
            }

            this.eventServicesMap[service]['averageConnectionTime'] = averageTime;
            this.eventServicesMap[service]['connectionInitialized'] = 0;
          }
          this.eventServicesMap[service]['connectionEstablished'] = noOfConnections;
          break;
        }
        case 'shouldBeUsed':
          this.eventServicesMap[service][event] = Boolean(options?.isEnabled);
          break;
        case 'terminated':
          this.eventServicesMap[service][event] = Boolean(options?.isEnabled);
          break;
        case 'bufferStart':
          if (options?.timestamp) {
            this.eventServicesMap[service]['bufferStart'] = options.timestamp;
          }
          break;
        case 'bufferEnd': {
          const bufferStart = Number(this.eventServicesMap[service]['bufferStart']);
          if (options?.timestamp && bufferStart > 0) {
            const duration = options.timestamp - bufferStart;
            const newCount = Number(this.eventServicesMap[service]['bufferCount']) + 1;
            const newTotal = Number(this.eventServicesMap[service]['totalBufferDuration']) + duration;
            this.eventServicesMap[service]['bufferCount'] = newCount;
            this.eventServicesMap[service]['totalBufferDuration'] = newTotal;
            this.eventServicesMap[service]['bufferStart'] = 0;
          }
          break;
        }
        case 'fpsDrop': {
          const newDropCount = Number(this.eventServicesMap[service]['fpsDropCount']) + 1;
          const newDroppedFrames =
            Number(this.eventServicesMap[service]['droppedFrames']) + (options?.droppedFrames ?? 0);
          this.eventServicesMap[service]['fpsDropCount'] = newDropCount;
          this.eventServicesMap[service]['droppedFrames'] = newDroppedFrames;
          break;
        }
        case 'liveLatency': {
          if (options?.latency !== undefined && options.latency >= 0) {
            const n = Number(this.eventServicesMap[service]['latencySampleCount']) + 1;
            const prevAvg = Number(this.eventServicesMap[service]['avgLiveLatency']);
            const newAvg = prevAvg + (options.latency - prevAvg) / n;
            this.eventServicesMap[service]['liveLatency'] = options.latency;
            this.eventServicesMap[service]['avgLiveLatency'] = newAvg;
            this.eventServicesMap[service]['latencySampleCount'] = n;
          }
          break;
        }
        case 'seekCount':
          this.eventServicesMap[service]['seekCount'] = Number(this.eventServicesMap[service]['seekCount']) + 1;
          break;
        case 'nonFatalError':
          this.eventServicesMap[service]['nonFatalErrorCount'] =
            Number(this.eventServicesMap[service]['nonFatalErrorCount']) + 1;
          break;
        case 'fragLoaded': {
          if (options?.loadTime !== undefined && options.loadTime > 0) {
            const n = Number(this.eventServicesMap[service]['fragLoadTimeSamples']) + 1;
            const prevAvg = Number(this.eventServicesMap[service]['avgFragLoadTime']);
            const newAvg = prevAvg + (options.loadTime - prevAvg) / n;
            this.eventServicesMap[service]['fragLoadTimeTotal'] =
              Number(this.eventServicesMap[service]['fragLoadTimeTotal']) + options.loadTime;
            this.eventServicesMap[service]['fragLoadTimeSamples'] = n;
            this.eventServicesMap[service]['avgFragLoadTime'] = newAvg;
          }
          break;
        }
        case 'levelSwitchCount':
          this.eventServicesMap[service]['levelSwitchCount'] =
            Number(this.eventServicesMap[service]['levelSwitchCount']) + 1;
          break;
        case 'levelSwitchDownCount':
          this.eventServicesMap[service]['levelSwitchDownCount'] =
            Number(this.eventServicesMap[service]['levelSwitchDownCount']) + 1;
          break;
        case 'playbackRestart':
          this.eventServicesMap[service]['playbackRestartCount'] =
            Number(this.eventServicesMap[service]['playbackRestartCount']) + 1;
          break;
        case 'forwardSkipCount':
          this.eventServicesMap[service]['forwardSkipCount'] =
            Number(this.eventServicesMap[service]['forwardSkipCount']) + 1;
          break;
        case 'rewindCount':
          this.eventServicesMap[service]['rewindCount'] = Number(this.eventServicesMap[service]['rewindCount']) + 1;
          break;
        case 'pauseCount':
          this.eventServicesMap[service]['pauseCount'] = Number(this.eventServicesMap[service]['pauseCount']) + 1;
          break;
        case 'speedChangeCount':
          this.eventServicesMap[service]['speedChangeCount'] =
            Number(this.eventServicesMap[service]['speedChangeCount']) + 1;
          break;
        case 'syncCorrectionCount':
          this.eventServicesMap[service]['syncCorrectionCount'] =
            Number(this.eventServicesMap[service]['syncCorrectionCount']) + 1;
          break;
        default:
          loggerService.log('[Tracker Service] Event does not exist', service, event);
          break;
      }
    } catch (error) {
      loggerService.error('[Tracker Service] Error', error);
    }
  }

  public getServiceSnapshot(service: Service): Record<Events, EventRecord> | null {
    return this.eventServicesMap[service] ? { ...this.eventServicesMap[service] } : null;
  }

  public addService(service: Service) {
    if (!this.eventServicesMap[service]) {
      this.eventServicesMap[service] = this.initEventsData();
      loggerService.info('[Tracker Service] Service added', service);
      return;
    }
    loggerService.info('[Tracker Service] Event already exists', service);
  }

  public pushData(services: Service[]) {
    loggerService.log('eventSubscribersMap', JSON.stringify(this.eventServicesMap));

    const store = getStore();
    if (!store) {
      loggerService.warn('[Tracker Service] Store not available, skipping pushData');
      return;
    }

    const isOrganizerOrSpeaker = store.getters['event/isOrganizerOrSpeaker'];
    const isTechCheckDone = getValueFromLocalstore(
      `${store.state.route?.params?.eventID}${
        isOrganizerOrSpeaker ? LOCAL_STORAGE_KEYS.speakerTechCheck : LOCAL_STORAGE_KEYS.attendeeTechCheck
      }`,
      false
    );

    const sessionDuration: Number = (Date.now() - this.initializationTime) / 1000;
    const isLiteModeEnabled = store.state.user.liteMode.liteModeActive;
    const userRole = store.state.user.currentEventUser?.event_role;

    const servicesData = services.reduce((acc, cur) => {
      acc[cur] = this.eventServicesMap[cur];
      return acc;
    }, {});

    const hasRelevantData = services.find(service => this.eventServicesMap[service].shouldBeUsed);

    if (!hasRelevantData) {
      return;
    }

    analytics.track('event_app_tracker', {
      eventServicesMap: servicesData,
      user: {
        email: store.state.user.currentUser?.email,
        name: store.state.user.currentEventUser?.name,
        id: store.state.user.currentUser?.id,
        logrocketURL: window.logrocketSessionUrl,
        isTechCheckDone,
        isLiteModeEnabled,
        role: userRole
      },
      sessionDuration,
      eventId: store.state.route.params?.eventID
    });
  }
  pushSessionData() {
    const sessionServices: Service[] = ['agora', 'inHouseSimulive', 'liveHybridVideo'];
    this.pushData(sessionServices);
    sessionServices.forEach((service: Service) => {
      this.eventServicesMap[service] = this.initEventsData();
    });
  }
  pushAllData() {
    this.pushData.bind(this)([
      'apigateway',
      'mattermost',
      'agora',
      'serverSentEvent',
      'inHouseSimulive',
      'onDemandVideo',
      'liveHybridVideo'
    ]);
  }
}

export default new TrackerService();
